import {
  CreatePreRegisteredUsersMutation,
  FailedUserInfo,
  InviteUsersMutation,
  PreRegisteredUsersDocument,
  useCreatePreRegisteredUsersMutation,
  useGetAuth0ConnectionsQuery,
  useInviteUsersMutation,
  AllUsersForUserManagementDocument,
} from '@/__generated__/graphql';
import { GenericModal } from '@/components/shared/GenericModal';
import { LoadingButton } from '@/components/shared/LoadingButton';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { GenericModalProps, InviteUsersState } from '@/types';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useAuth0 } from '@auth0/auth0-react';
import { Button, DialogActions, DialogContent, LinearProgress, Tab, Tabs } from '@mui/material';
import * as Sentry from '@sentry/browser';
import { useCallback, useMemo, useState } from 'react';
import { dialogOverlay } from '../../styles';
import { InviteConfirmationDialog } from './InviteConfirmationDialog';
import { InviteInputs } from './InviteInputs';
import { PreRegisteredUsers } from './PreRegisteredUsers';

type InviteUsersModalProps = GenericModalProps;
type TabChangeEvent = React.ChangeEvent<{}>;

enum SnackbarSeverity {
  Success = 'success',
  Error = 'error',
  Info = 'info',
  Warning = 'warning',
}

const initialInviteUsersState = {
  emails: [],
  roles: [],
  groups: [],
};

export const InviteUsersModal = ({ open, handleClose }: InviteUsersModalProps) => {
  const currentUser = useAuth0().user;

  const [inviteUsersData, setInviteUsersData] = useState<InviteUsersState>(initialInviteUsersState);

  const [isBlurred, setIsBlurred] = useState(false);
  const [tabValue, setTabValue] = useState<number>(0);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const [inviteUsers, { loading }] = useInviteUsersMutation();

  const [createPreRegisteredUsers, { loading: createLoading }] =
    useCreatePreRegisteredUsersMutation();

  const { data: connections, loading: connectionsLoading } = useGetAuth0ConnectionsQuery({
    variables: {
      applicationId: import.meta.env.VITE_APP_AUTH0_CLIENT_ID || '',
    },
    skip: !open,
  });

  const getDefaultConnection = useCallback(() => {
    return (
      connections?.auth0Connections.find((connection) =>
        connection.name.toLowerCase().includes('user')
      )?.name ?? ''
    );
  }, [connections]);

  const disableButton = useMemo(() => {
    const { emails, roles, groups } = inviteUsersData;
    return !(emails.length > 0 && roles.length > 0 && groups.length > 0);
  }, [inviteUsersData]);

  const handleClearModal = useCallback(() => {
    handleClose();
    setTabValue(0);
    setInviteUsersData(initialInviteUsersState);
    setIsBlurred(false);
  }, [handleClose]);

  const generateMessage = useCallback(
    (response: InviteUsersMutation) => {
      const users = response.inviteUsers.createdUsers;
      const failedUsers = response.inviteUsers.failedUsers || [];

      const successCount = users.length;
      const successMessage = successCount > 0 ? `Invited ${successCount} user(s) successfully` : '';

      if (failedUsers.length === 0) {
        return { message: successMessage, severity: SnackbarSeverity.Success };
      }

      const failedMessage = failedUsers.reduce((message, user: FailedUserInfo | null) => {
        if (user) {
          Sentry.captureMessage(`Failed to invite users: ${user.errorMessage}`, {
            user: { sub: currentUser?.sub },
            extra: {
              emails: JSON.stringify(inviteUsersData.emails),
              roles: JSON.stringify(inviteUsersData.roles),
              groups: JSON.stringify(inviteUsersData.groups),
              connection: getDefaultConnection(),
              clientId: import.meta.env.VITE_APP_AUTH0_CLIENT_ID,
            },
          });
          return `${message}${user.email}: ${user.errorMessage} \n`;
        }
        return message;
      }, 'Failed to create the following user(s) \n');
      if (successCount > 0) {
        const warningMessage =
          failedMessage + `\nRemaining ${successCount} user(s) invited successfully`;
        return { message: warningMessage, severity: SnackbarSeverity.Warning };
      }
      return { message: failedMessage, severity: SnackbarSeverity.Error };
    },
    [
      currentUser?.sub,
      getDefaultConnection,
      inviteUsersData.emails,
      inviteUsersData.groups,
      inviteUsersData.roles,
    ]
  );

  const generateSSOMessage = useCallback(
    (response: CreatePreRegisteredUsersMutation) => {
      const users = response.createPreRegisteredUsers.createdUsers;
      const failedUsers = response.createPreRegisteredUsers.failedUsers || [];

      const successCount = users.length;
      const successMessage = successCount > 0 ? `Invited ${successCount} user(s) successfully` : '';

      if (failedUsers.length === 0) {
        return { message: successMessage, severity: SnackbarSeverity.Success };
      }

      const failedMessage = failedUsers.reduce((message, user: FailedUserInfo | null) => {
        if (user) {
          Sentry.captureMessage(`Failed to invite users: ${user.errorMessage}`, {
            user: { sub: currentUser?.sub },
            extra: {
              emails: JSON.stringify(inviteUsersData.emails),
              roles: JSON.stringify(inviteUsersData.roles),
              groups: JSON.stringify(inviteUsersData.groups),
              connection: getDefaultConnection(),
              clientId: import.meta.env.VITE_APP_AUTH0_CLIENT_ID,
            },
          });
          return `${message}${user.email}: ${user.errorMessage} \n`;
        }
        return message;
      }, 'Failed to create the following user(s) \n');
      if (successCount > 0) {
        const warningMessage =
          failedMessage + `\nRemaining ${successCount} user(s) invited successfully`;
        return { message: warningMessage, severity: SnackbarSeverity.Warning };
      }
      return { message: failedMessage, severity: SnackbarSeverity.Error };
    },
    [
      currentUser?.sub,
      getDefaultConnection,
      inviteUsersData.emails,
      inviteUsersData.groups,
      inviteUsersData.roles,
    ]
  );

  const handleInviteUserConfirmation = useCallback(() => {
    setOpenConfirmationDialog(true);
  }, []);

  const handleSendInviteUsers = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement> | undefined) => {
      event?.preventDefault();
      await inviteUsers({
        variables: {
          input: {
            ...inviteUsersData,
            connection: getDefaultConnection(),
            clientId: import.meta.env.VITE_APP_AUTH0_CLIENT_ID,
          },
        },
        refetchQueries: [AllUsersForUserManagementDocument],
        awaitRefetchQueries: true,
        onCompleted: (response) => {
          const snackbarText = generateMessage(response);
          showSnackBar(snackbarText.message, snackbarText.severity);
        },
        onError: (error) => {
          Sentry.captureMessage(`Failed to invite users: ${error.message}`, {
            user: { sub: currentUser?.sub },
            extra: {
              emails: JSON.stringify(inviteUsersData.emails),
              roles: JSON.stringify(inviteUsersData.roles),
              groups: JSON.stringify(inviteUsersData.groups),
              connection: getDefaultConnection(),
              clientId: import.meta.env.VITE_APP_AUTH0_CLIENT_ID,
            },
          });
          showSnackBar(`Failed to invite users: ${error.message}`, SnackbarSeverity.Error);
        },
      });
      handleClearModal();
    },
    [
      inviteUsers,
      inviteUsersData,
      getDefaultConnection,
      handleClearModal,
      generateMessage,
      showSnackBar,
      currentUser?.sub,
    ]
  );

  const handleTabChange = (event: TabChangeEvent, newValue: number) => {
    setTabValue(newValue);
    setInviteUsersData((previousData) => ({
      emails: [],
      groups: previousData.groups,
      roles: previousData.roles,
    }));

    setIsBlurred(false);
  };

  const handleInvitePreRegistrationConfirmation = useCallback(() => {
    setOpenConfirmationDialog(true);
  }, []);

  const handleCreatePreRegisteredUsers = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement> | undefined) => {
      event?.preventDefault();
      const { roles, ...payload } = inviteUsersData;
      await createPreRegisteredUsers({
        variables: {
          input: {
            roleIds: roles,
            ...payload,
          },
        },
        refetchQueries: [PreRegisteredUsersDocument, AllUsersForUserManagementDocument],
        awaitRefetchQueries: true,
        onCompleted: (response) => {
          const snackbarText = generateSSOMessage(response);
          showSnackBar(snackbarText.message, SnackbarSeverity.Success);
        },
        onError: (error) => {
          Sentry.captureMessage(`Failed to pre-register user(s): ${error.message}`, {
            user: { sub: currentUser?.sub },
            extra: {
              roleIds: JSON.stringify(roles),
              email: JSON.stringify(payload.emails),
              groups: JSON.stringify(payload.groups),
            },
          });
          showSnackBar(`Failed to pre-register user(s): ${error.message}`, SnackbarSeverity.Error);
        },
      });
      handleClearModal();
    },
    [
      createPreRegisteredUsers,
      currentUser?.sub,
      generateSSOMessage,
      handleClearModal,
      inviteUsersData,
      showSnackBar,
    ]
  );

  const confirmationDialog = useMemo(() => {
    const description =
      tabValue === 0
        ? 'Email invites aren’t applicable for SSO users. To invite a user who will log in with Single Sign-On (SSO), please use the SSO tab instead.'
        : 'SSO invites aren’t applicable for users logging in with email and password. To invite these users, please use the Email Invite tab instead.';
    return (
      <InviteConfirmationDialog
        title={`Not applicable for ${tabValue === 0 ? 'SSO' : 'Email invite'} users`}
        description={description}
        confirmButtonText={`Proceed with ${tabValue === 0 ? 'email' : 'SSO'} invites`}
        handleClose={() => setOpenConfirmationDialog(false)}
        isOpen={openConfirmationDialog}
        onConfirm={tabValue === 0 ? handleSendInviteUsers : handleCreatePreRegisteredUsers}
      />
    );
  }, [handleCreatePreRegisteredUsers, handleSendInviteUsers, openConfirmationDialog, tabValue]);

  return (
    <GenericModal
      sx={{
        ...dialogOverlay,
      }}
      open={open}
      handleClose={handleClearModal}
      title="Invite new users"
      crossIconStyle={{ backgroundColor: 'transparent', color: 'primary' }}
      backgroundPaperStyle={{ height: 'auto !important' }}
    >
      <DialogContent>
        <Tabs variant="fullWidth" value={tabValue} onChange={handleTabChange}>
          <Tab label="EMAIL INVITE" />
          <Tab label="SINGLE SIGN-ON USER" />
        </Tabs>
        {connectionsLoading && <LinearProgress />}
        <InviteInputs
          inviteUsersData={inviteUsersData}
          setInviteUsersData={setInviteUsersData}
          isBlurred={isBlurred}
          setIsBlurred={setIsBlurred}
          tabValue={tabValue}
        />
        {tabValue === 1 && <PreRegisteredUsers />}
      </DialogContent>
      <DialogActions sx={{ borderTop: 1, borderColor: 'divider', mt: 1 }}>
        <Button onClick={handleClearModal} variant="text" sx={textButtonStyle}>
          Cancel
        </Button>
        <LoadingButton
          loading={loading || createLoading}
          buttonVariant="contained"
          buttonStyles={containedButtonStyle}
          onClick={
            tabValue === 0 ? handleInviteUserConfirmation : handleInvitePreRegistrationConfirmation
          }
          disabled={disableButton || loading}
        >
          {tabValue === 0 ? 'Send Invites' : 'Create Users'}
        </LoadingButton>
      </DialogActions>
      {confirmationDialog}
    </GenericModal>
  );
};
